import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { GOOGLE_TRACKING_READY_EVENT, hasOptionalConsent, isPrivateAnalyticsPath } from '@/lib/analyticsConsent';

const normaliseName = (value, fallback) => {
  const name = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
  return name || fallback;
};

const getLocation = (element) => {
  const region = element.closest('[data-ga-location], section[id], header, footer, nav, main, [role="dialog"]');
  if (!region) return 'page';
  if (region.dataset?.gaLocation) return normaliseName(region.dataset.gaLocation, 'page');
  if (region.id) return normaliseName(region.id, 'page');
  if (region.getAttribute('role') === 'dialog') return 'dialog';
  return region.tagName.toLowerCase();
};

const getDestination = (element) => {
  if (element.tagName !== 'A') return undefined;
  try {
    const url = new URL(element.href, window.location.href);
    return url.origin === window.location.origin
      ? `${url.pathname}${url.hash}`
      : `${url.hostname}${url.pathname}`;
  } catch (_error) {
    return undefined;
  }
};

const checkoutButtonNames = new Set([
  'get_instant_access',
  'get_data_pack',
  'get_privacy_pack',
  'go_unlimited',
  'go_unlimited_and_vpn',
  'go_unlimited_vpn',
  'select_plan',
  'get_united_kingdom_number_instantly',
  'get_uk_number_instantly',
  'get_usa_number_instantly',
]);

const paymentButtonNames = new Set([
  'pay_via_phone_bill_uk_only',
  'pay_with_card_stripe',
  'pay_with_paypal',
]);

const getCommerceContext = (element, buttonLocation) => {
  const productName = String(element.dataset.productName || '').trim().slice(0, 120);
  const rawProductId = element.dataset.productId || productName;
  const productId = rawProductId ? normaliseName(rawProductId, 'phantompath_service') : '';
  const rawValue = Number.parseFloat(String(element.dataset.value || '').replace(/[^0-9.]/g, ''));
  const value = Number.isFinite(rawValue) && rawValue >= 0 ? Number(rawValue.toFixed(2)) : undefined;
  const currency = productName || element.dataset.currency
    ? String(element.dataset.currency || 'GBP').toUpperCase().slice(0, 3)
    : '';
  const paymentType = normaliseName(element.dataset.paymentMethod, '');

  const eventContext = {};
  if (productName) eventContext.product_name = productName;
  if (productId) eventContext.product_id = productId;
  if (value !== undefined) eventContext.value = value;
  if (currency) eventContext.currency = currency;
  if (paymentType) eventContext.payment_type = paymentType;

  const gaCommerce = productName ? {
    currency,
    ...(value !== undefined ? { value } : {}),
    items: [{
      item_id: productId,
      item_name: productName,
      item_category: buttonLocation,
      ...(value !== undefined ? { price: value } : {}),
      quantity: 1,
    }],
  } : {};

  const advertisingCommerce = productName ? {
    content_ids: [productId],
    content_id: productId,
    content_name: productName,
    content_type: 'product',
    currency,
    ...(value !== undefined ? { value, price: value } : {}),
    contents: [{
      id: productId,
      content_id: productId,
      quantity: 1,
      ...(value !== undefined ? { item_price: value, price: value } : {}),
    }],
  } : {};

  return { eventContext, gaCommerce, advertisingCommerce };
};

const GoogleAnalytics = () => {
  const location = useLocation();
  const lastTrackedPath = useRef(null);

  useEffect(() => {
    const trackingPath = `${location.pathname}${location.search}`;
    if (isPrivateAnalyticsPath(location.pathname)) {
      lastTrackedPath.current = trackingPath;
      return;
    }
    const trackPageView = () => {
      if (!window.gtag || lastTrackedPath.current === trackingPath) return;
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_location: `${window.location.origin}${trackingPath}`,
        page_path: trackingPath,
      });
      lastTrackedPath.current = trackingPath;
    };
    trackPageView();
    window.addEventListener(GOOGLE_TRACKING_READY_EVENT, trackPageView);
    return () => window.removeEventListener(GOOGLE_TRACKING_READY_EVENT, trackPageView);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const trackClick = (event) => {
      if (isPrivateAnalyticsPath(window.location.pathname)) return;
      if (!(event.target instanceof Element)) return;
      const element = event.target.closest('button, a');
      if (!element || element.matches(':disabled, [aria-disabled="true"]')) return;

      const readableName = element.dataset.gaName
        || element.getAttribute('aria-label')
        || element.getAttribute('title')
        || element.textContent;

      const buttonName = normaliseName(readableName, element.tagName === 'A' ? 'unnamed_link' : 'unnamed_button');
      const buttonLocation = getLocation(element);
      const commerceStage = normaliseName(element.dataset.gaEvent, '');
      const elementType = element.tagName === 'A' ? 'link' : 'button';
      const linkDestination = getDestination(element);
      const commerce = getCommerceContext(element, buttonLocation);
      const eventParameters = {
        button_name: buttonName,
        button_location: buttonLocation,
        element_type: elementType,
        link_destination: linkDestination,
        ...commerce.eventContext,
      };

      window.gtag?.('event', 'button_click', eventParameters);
      if (hasOptionalConsent()) {
        window.fbq?.('trackCustom', 'ButtonClick', eventParameters);
        window.ttq?.track?.('ClickButton', eventParameters);
      }

      if (commerceStage === 'begin_checkout' || checkoutButtonNames.has(buttonName)) {
        window.gtag?.('event', 'begin_checkout', {
          content_name: buttonName,
          content_category: buttonLocation,
          ...commerce.gaCommerce,
        });
        if (hasOptionalConsent()) {
          window.fbq?.('track', 'InitiateCheckout', {
            content_name: buttonName,
            content_category: buttonLocation,
            ...commerce.advertisingCommerce,
          });
          window.ttq?.track?.('AddToCart', {
            content_name: buttonName,
            content_category: buttonLocation,
            ...commerce.advertisingCommerce,
          });
          window.ttq?.track?.('InitiateCheckout', {
            content_name: buttonName,
            content_category: buttonLocation,
            ...commerce.advertisingCommerce,
          });
        }
      }

      if (commerceStage === 'add_payment_info' || paymentButtonNames.has(buttonName)) {
        window.gtag?.('event', 'add_payment_info', {
          payment_type: commerce.eventContext.payment_type || buttonName,
          content_category: buttonLocation,
          ...commerce.gaCommerce,
        });
        if (hasOptionalConsent()) {
          window.fbq?.('track', 'AddPaymentInfo', {
            content_name: buttonName,
            content_category: buttonLocation,
            ...commerce.advertisingCommerce,
          });
          window.ttq?.track?.('AddPaymentInfo', {
            content_name: buttonName,
            content_category: buttonLocation,
            ...commerce.advertisingCommerce,
          });
        }
      }
    };

    document.addEventListener('click', trackClick);
    return () => document.removeEventListener('click', trackClick);
  }, []);

  return null;
};

export default GoogleAnalytics;
